/**
 * THE MASTER STORY CHAIN
 * ──────────────────────────────────────────────────────────────────────────
 * One long ordered list connecting every story in the library.
 *
 * HOW IT WORKS
 *   When a reader finishes a story, the app looks up the current story's
 *   `file` path in this array, then serves the very next entry as the
 *   "Up Next" card.  No manual wiring per-story — just position in this list.
 *
 * HOW TO ADD A NEW STORY
 *   1. Insert its manifest `file` path at the desired position in this array.
 *   2. That's it.  The stories before and after it in the array will
 *      automatically chain to/from it.
 *
 * CONVENTIONS
 *   • Within a series, episodes must appear consecutively in order.
 *   • Stories progress HSK1 → HSK2 → … → HSK6.
 *   • Standalones go at the END of their HSK level block (after all series).
 *   • Use the exact `file` string from the manifest (no leading slash).
 * ──────────────────────────────────────────────────────────────────────────
 */

export const STORY_CHAIN = [

  // ── HSK 1 ────────────────────────────────────────────────────────────────

  // Huangshan Adventure  (series · 6 episodes)
  'HSK1/Huangshan/Huangshan_Adventure_1.json',
  'HSK1/Huangshan/Huangshan_Adventure_2.json',
  'HSK1/Huangshan/Huangshan_Adventure_3.json',
  'HSK1/Huangshan/Huangshan_Adventure_4.json',
  'HSK1/Huangshan/Huangshan_Adventure_5.json',
  'HSK1/Huangshan/Huangshan_Adventure_6.json',

  // Daming  (series · 10 episodes)
  'HSK1/Daming/Daming_01.json',
  'HSK1/Daming/Daming_02.json',
  'HSK1/Daming/Daming_03.json',
  'HSK1/Daming/Daming_04.json',
  'HSK1/Daming/Daming_05.json',
  'HSK1/Daming/Daming_06.json',
  'HSK1/Daming/Daming_07.json',
  'HSK1/Daming/Daming_08.json',
  'HSK1/Daming/Daming_09.json',
  'HSK1/Daming/Daming_10.json',

  // Little Animals  (series · 5 episodes)
  'HSK1/Little_Animals/Little_Animals_01.json',
  'HSK1/Little_Animals/Little_Animals_02.json',
  'HSK1/Little_Animals/Little_Animals_03.json',
  'HSK1/Little_Animals/Little_Animals_04.json',
  'HSK1/Little_Animals/Little_Animals_05.json',

  // Mapo Tofu  (series · 3 parts)
  'HSK1/Mapo_tofu/part_1.json',
  'HSK1/Mapo_tofu/part_2.json',
  'HSK1/Mapo_tofu/part_3.json',

  // HSK 1 standalones  ↓ add new HSK1 stories above this line
  'HSK1/UNCATEGORISED/I_live_in_Australia.json',
  'HSK1/UNCATEGORISED/I_love_my_girlfriend.json',
  'HSK1/UNCATEGORISED/Mushroom_hotpot.json',
  'HSK1/UNCATEGORISED/Art_Class.json',
  'HSK1/UNCATEGORISED/My_Little_Garden.json',

  // ── HSK 2 ────────────────────────────────────────────────────────────────

  // Beijing  (series · 3 episodes — morning → afternoon → evening)
  'HSK2/Beijing/morning.json',
  'HSK2/Beijing/afternoon.json',
  'HSK2/Beijing/evening.json',

  // Friendship  (series · 5 episodes)
  'HSK2/Friendship/Friendship_01.json',
  'HSK2/Friendship/Friendship_02.json',
  'HSK2/Friendship/Friendship_03.json',
  'HSK2/Friendship/Friendship_04.json',
  'HSK2/Friendship/Friendship_05.json',

  // New Year  (series · 10 episodes)
  'HSK2/New_Year/New_Year_01.json',
  'HSK2/New_Year/New_Year_02.json',
  'HSK2/New_Year/New_Year_03.json',
  'HSK2/New_Year/New_Year_04.json',
  'HSK2/New_Year/New_Year_05.json',
  'HSK2/New_Year/New_Year_06.json',
  'HSK2/New_Year/New_Year_07.json',
  'HSK2/New_Year/New_Year_08.json',
  'HSK2/New_Year/New_Year_09.json',
  'HSK2/New_Year/New_Year_10.json',

  // Strange Phone  (series · 10 episodes)
  'HSK2/Strange_Phone/Strange_Phone_01.json',
  'HSK2/Strange_Phone/Strange_Phone_02.json',
  'HSK2/Strange_Phone/Strange_Phone_03.json',
  'HSK2/Strange_Phone/Strange_Phone_04.json',
  'HSK2/Strange_Phone/Strange_Phone_05.json',
  'HSK2/Strange_Phone/Strange_Phone_06.json',
  'HSK2/Strange_Phone/Strange_Phone_07.json',
  'HSK2/Strange_Phone/Strange_Phone_08.json',
  'HSK2/Strange_Phone/Strange_Phone_09.json',
  'HSK2/Strange_Phone/Strange_Phone_10.json',

  // Summer Holiday  (series · 10 episodes)
  'HSK2/Summer_Holiday/Summer_Holiday_01.json',
  'HSK2/Summer_Holiday/Summer_Holiday_02.json',
  'HSK2/Summer_Holiday/Summer_Holiday_03.json',
  'HSK2/Summer_Holiday/Summer_Holiday_04.json',
  'HSK2/Summer_Holiday/Summer_Holiday_05.json',
  'HSK2/Summer_Holiday/Summer_Holiday_06.json',
  'HSK2/Summer_Holiday/Summer_Holiday_07.json',
  'HSK2/Summer_Holiday/Summer_Holiday_08.json',
  'HSK2/Summer_Holiday/Summer_Holiday_09.json',
  'HSK2/Summer_Holiday/Summer_Holiday_10.json',

  // HSK 2 standalones  ↓ add new HSK2 stories above this line
  'HSK2/UNCATEGORISED/Tingting_the_puppy.json',
  'HSK2/UNCATEGORISED/The_Alien_Visitor.json',

  // ── HSK 3 ────────────────────────────────────────────────────────────────

  // A Busy Weekend  (series · 10 episodes)
  'HSK3/A_busy_weekend/1.json',
  'HSK3/A_busy_weekend/2.json',
  'HSK3/A_busy_weekend/3.json',
  'HSK3/A_busy_weekend/4.json',
  'HSK3/A_busy_weekend/5.json',
  'HSK3/A_busy_weekend/6.json',
  'HSK3/A_busy_weekend/7.json',
  'HSK3/A_busy_weekend/8.json',
  'HSK3/A_busy_weekend/9.json',
  'HSK3/A_busy_weekend/10.json',

  // Huangshan  (series · 1 episode — HSK3 version)
  'HSK3/Huangshan/mountain_trip.json',

  // Mermaid  (series · 5 episodes)
  'HSK3/Mermaid/Mermaid_01.json',
  'HSK3/Mermaid/Mermaid_02.json',
  'HSK3/Mermaid/Mermaid_03.json',
  'HSK3/Mermaid/Mermaid_04.json',
  'HSK3/Mermaid/Mermaid_05.json',

  // Mulan  (series · 5 episodes)
  'HSK3/Mulan/Mulan_01.json',
  'HSK3/Mulan/Mulan_02.json',
  'HSK3/Mulan/Mulan_03.json',
  'HSK3/Mulan/Mulan_04.json',
  'HSK3/Mulan/Mulan_05.json',

  // The Road Home  (series · 5 episodes)
  'HSK3/The_Road_Home/The_Road_Home_01.json',
  'HSK3/The_Road_Home/The_Road_Home_02.json',
  'HSK3/The_Road_Home/The_Road_Home_03.json',
  'HSK3/The_Road_Home/The_Road_Home_04.json',
  'HSK3/The_Road_Home/The_Road_Home_05.json',

  // Forest Girl Book 1  (series · 3 episodes)
  'HSK3/Forest_Girl_Book_1/Part_1.json',
  'HSK3/Forest_Girl_Book_1/Part_2.json',
  'HSK3/Forest_Girl_Book_1/Part_3.json',

  // Forest Girl Book 2  (series · 3 episodes)
  'HSK3/Forest_Girl_Book_2/Part_1.json',
  'HSK3/Forest_Girl_Book_2/Part_2.json',
  'HSK3/Forest_Girl_Book_2/Part_3.json',

  // Forest Girl Book 3  (series · 4 episodes)
  'HSK3/Forest_Girl_Book_3/Part_1.json',
  'HSK3/Forest_Girl_Book_3/Part_2.json',
  'HSK3/Forest_Girl_Book_3/Part_3.json',
  'HSK3/Forest_Girl_Book_3/Part_4.json',

  // HSK 3 standalones  ↓ add new HSK3 stories above this line
  'HSK3/UNCATEGORISED/A_trip_to_the_market.json',
  'HSK3/UNCATEGORISED/Making_dumplings_with_family.json',
  'HSK3/UNCATEGORISED/Pool_party.json',
  'HSK3/UNCATEGORISED/Where_is_that_smell.json',

  // ── HSK 4 ────────────────────────────────────────────────────────────────

  // Lily  (series · 5 episodes)
  'HSK4/Lily/Lily_01.json',
  'HSK4/Lily/Lily_02.json',
  'HSK4/Lily/Lily_03.json',
  'HSK4/Lily/Lily_04.json',
  'HSK4/Lily/Lily_05.json',

  // Living in Shanghai  (series · 10 episodes)
  'HSK4/Living_in_Shanghai/episode_201.json',
  'HSK4/Living_in_Shanghai/episode_202.json',
  'HSK4/Living_in_Shanghai/episode_203.json',
  'HSK4/Living_in_Shanghai/episode_204.json',
  'HSK4/Living_in_Shanghai/episode_205.json',
  'HSK4/Living_in_Shanghai/episode_206.json',
  'HSK4/Living_in_Shanghai/episode_207.json',
  'HSK4/Living_in_Shanghai/episode_208.json',
  'HSK4/Living_in_Shanghai/episode_209.json',
  'HSK4/Living_in_Shanghai/episode_210.json',

  // New Job  (series · 5 episodes)
  'HSK4/New_Job/New_Job_01.json',
  'HSK4/New_Job/New_Job_02.json',
  'HSK4/New_Job/New_Job_03.json',
  'HSK4/New_Job/New_Job_04.json',
  'HSK4/New_Job/New_Job_05.json',

  // HSK 4 standalones  ↓ add new HSK4 stories above this line
  'HSK4/UNCATEGORISED/Dried_hawthorn.json',
  'HSK4/UNCATEGORISED/Kunming_part_1.json',
  'HSK4/UNCATEGORISED/My_favourite_rice_cooker.json',
  'HSK4/UNCATEGORISED/Planning_trip.json',
  'HSK4/UNCATEGORISED/Tingting_helping.json',

  // ── HSK 5 ────────────────────────────────────────────────────────────────

  // HSK 5 standalones  ↓ add new HSK5 stories above this line
  'HSK5/UNCATEGORISED/Making_dumplings_with_colleagues.json',
  'HSK5/UNCATEGORISED/Tingting_funny_idea.json',

  // ── HSK 6 ────────────────────────────────────────────────────────────────

  // HSK 6 standalones  ↓ add new HSK6 stories above this line
  'HSK6/UNCATEGORISED/Bondi_beach.json',
  'HSK6/UNCATEGORISED/Tingting_emotional.json',

  // ── First 1000 Characters Course ─────────────────────────────────────────

  // Newbie  (Series 1 · episodes 1–10)
  'First_1000_Characters_Course/Newbie/S001.json',
  'First_1000_Characters_Course/Newbie/S002.json',
  'First_1000_Characters_Course/Newbie/S003.json',
  'First_1000_Characters_Course/Newbie/S004.json',
  'First_1000_Characters_Course/Newbie/S005.json',
  'First_1000_Characters_Course/Newbie/S006.json',
  'First_1000_Characters_Course/Newbie/S007.json',
  'First_1000_Characters_Course/Newbie/S008.json',
  'First_1000_Characters_Course/Newbie/S009.json',
  'First_1000_Characters_Course/Newbie/S010.json',

  // Elementary  (Series 2 · episodes 1–20)
  'First_1000_Characters_Course/Elementary/S011.json',
  'First_1000_Characters_Course/Elementary/S012.json',
  'First_1000_Characters_Course/Elementary/S013.json',
  'First_1000_Characters_Course/Elementary/S014.json',
  'First_1000_Characters_Course/Elementary/S015.json',
  'First_1000_Characters_Course/Elementary/S016.json',
  'First_1000_Characters_Course/Elementary/S017.json',
  'First_1000_Characters_Course/Elementary/S018.json',
  'First_1000_Characters_Course/Elementary/S019.json',
  'First_1000_Characters_Course/Elementary/S020.json',
  'First_1000_Characters_Course/Elementary/S021.json',
  'First_1000_Characters_Course/Elementary/S022.json',
  'First_1000_Characters_Course/Elementary/S023.json',
  'First_1000_Characters_Course/Elementary/S024.json',
  'First_1000_Characters_Course/Elementary/S025.json',
  'First_1000_Characters_Course/Elementary/S026.json',
  'First_1000_Characters_Course/Elementary/S027.json',
  'First_1000_Characters_Course/Elementary/S028.json',
  'First_1000_Characters_Course/Elementary/S029.json',
  'First_1000_Characters_Course/Elementary/S030.json',

  // Intermediate  (Series 3 · episodes 1–30)
  'First_1000_Characters_Course/Intermediate/S031.json',
  'First_1000_Characters_Course/Intermediate/S032.json',
  'First_1000_Characters_Course/Intermediate/S033.json',
  'First_1000_Characters_Course/Intermediate/S034.json',
  'First_1000_Characters_Course/Intermediate/S035.json',
  'First_1000_Characters_Course/Intermediate/S036.json',
  'First_1000_Characters_Course/Intermediate/S037.json',
  'First_1000_Characters_Course/Intermediate/S038.json',
  'First_1000_Characters_Course/Intermediate/S039.json',
  'First_1000_Characters_Course/Intermediate/S040.json',
  'First_1000_Characters_Course/Intermediate/S041.json',
  'First_1000_Characters_Course/Intermediate/S042.json',
  'First_1000_Characters_Course/Intermediate/S043.json',
  'First_1000_Characters_Course/Intermediate/S044.json',
  'First_1000_Characters_Course/Intermediate/S045.json',
  'First_1000_Characters_Course/Intermediate/S046.json',
  'First_1000_Characters_Course/Intermediate/S047.json',
  'First_1000_Characters_Course/Intermediate/S048.json',
  'First_1000_Characters_Course/Intermediate/S049.json',
  'First_1000_Characters_Course/Intermediate/S050.json',
  'First_1000_Characters_Course/Intermediate/S051.json',
  'First_1000_Characters_Course/Intermediate/S052.json',
  'First_1000_Characters_Course/Intermediate/S053.json',
  'First_1000_Characters_Course/Intermediate/S054.json',
  'First_1000_Characters_Course/Intermediate/S055.json',
  'First_1000_Characters_Course/Intermediate/S056.json',
  'First_1000_Characters_Course/Intermediate/S057.json',
  'First_1000_Characters_Course/Intermediate/S058.json',
  'First_1000_Characters_Course/Intermediate/S059.json',
  'First_1000_Characters_Course/Intermediate/S060.json',

  // Upper Intermediate  (Series 4 · episodes 1–30)
  'First_1000_Characters_Course/Upper_Intermediate/S061.json',
  'First_1000_Characters_Course/Upper_Intermediate/S062.json',
  'First_1000_Characters_Course/Upper_Intermediate/S063.json',
  'First_1000_Characters_Course/Upper_Intermediate/S064.json',
  'First_1000_Characters_Course/Upper_Intermediate/S065.json',
  'First_1000_Characters_Course/Upper_Intermediate/S066.json',
  'First_1000_Characters_Course/Upper_Intermediate/S067.json',
  'First_1000_Characters_Course/Upper_Intermediate/S068.json',
  'First_1000_Characters_Course/Upper_Intermediate/S069.json',
  'First_1000_Characters_Course/Upper_Intermediate/S070.json',
  'First_1000_Characters_Course/Upper_Intermediate/S071.json',
  'First_1000_Characters_Course/Upper_Intermediate/S072.json',
  'First_1000_Characters_Course/Upper_Intermediate/S073.json',
  'First_1000_Characters_Course/Upper_Intermediate/S074.json',
  'First_1000_Characters_Course/Upper_Intermediate/S075.json',
  'First_1000_Characters_Course/Upper_Intermediate/S076.json',
  'First_1000_Characters_Course/Upper_Intermediate/S077.json',
  'First_1000_Characters_Course/Upper_Intermediate/S078.json',
  'First_1000_Characters_Course/Upper_Intermediate/S079.json',
  'First_1000_Characters_Course/Upper_Intermediate/S080.json',
  'First_1000_Characters_Course/Upper_Intermediate/S081.json',
  'First_1000_Characters_Course/Upper_Intermediate/S082.json',
  'First_1000_Characters_Course/Upper_Intermediate/S083.json',
  'First_1000_Characters_Course/Upper_Intermediate/S084.json',
  'First_1000_Characters_Course/Upper_Intermediate/S085.json',
  'First_1000_Characters_Course/Upper_Intermediate/S086.json',
  'First_1000_Characters_Course/Upper_Intermediate/S087.json',
  'First_1000_Characters_Course/Upper_Intermediate/S088.json',
  'First_1000_Characters_Course/Upper_Intermediate/S089.json',
  'First_1000_Characters_Course/Upper_Intermediate/S090.json',

  // Advanced  (Final Arc · episodes 1–10)
  'First_1000_Characters_Course/Advanced/S091.json',
  'First_1000_Characters_Course/Advanced/S092.json',
  'First_1000_Characters_Course/Advanced/S093.json',
  'First_1000_Characters_Course/Advanced/S094.json',
  'First_1000_Characters_Course/Advanced/S095.json',
  'First_1000_Characters_Course/Advanced/S096.json',
  'First_1000_Characters_Course/Advanced/S097.json',
  'First_1000_Characters_Course/Advanced/S098.json',
  'First_1000_Characters_Course/Advanced/S099.json',
  'First_1000_Characters_Course/Advanced/S100.json',

];

/**
 * Given a manifest `file` string, returns the next ENABLED entry in the chain,
 * skipping over any entries marked `disabled: true`.
 * Returns null if no enabled story follows in the chain, or the current
 * story isn't in the chain at all.
 *
 * @param {string} currentFile   - The manifest `file` field of the open story
 * @param {Array}  manifestList  - Full manifest array (including disabled entries)
 * @returns {object|null}  The next enabled manifest entry, or null
 */
export function getNextInChain(currentFile, manifestList) {
  if (!currentFile || !Array.isArray(manifestList)) return null;
  const idx = STORY_CHAIN.indexOf(currentFile);
  if (idx < 0) return null;
  // Walk forward, skipping disabled entries
  for (let i = idx + 1; i < STORY_CHAIN.length; i++) {
    const entry = manifestList.find(m => m.file === STORY_CHAIN[i]);
    if (entry && !entry.disabled) {
      if (entry.series) {
        // 1-based position of this file among all chain entries that share the same series
        const seriesChainFiles = STORY_CHAIN.filter(f => {
          const m = manifestList.find(e => e.file === f);
          return m && m.series === entry.series;
        });
        const epNum = seriesChainFiles.indexOf(STORY_CHAIN[i]) + 1;
        return { ...entry, _episodeNumber: epNum > 0 ? epNum : null };
      }
      return entry;
    }
  }
  return null;
}
